/**
 * ActivityBlock — a single timed part rendered as a block on one day's hour timeline.
 *
 * Absolutely positioned by start hour (top) and duration (height); horizontal lane placement (for
 * overlapping parts) comes from `--lane-index` / `--lane-count`. A block that starts before the visible
 * day window but ends inside it is drawn "clipped" (flat top with a hatch cut). Drag/resize gestures live
 * in {@link useBlockDrag}; a plain click opens the part in the drawer.
 */
import { useRef } from 'react';

import { HEAD_PAD, NIGHT_START, TYPE_COLOR, TypeIcon, fmtH, hoursToPx } from '../model';
import type { BoardPart } from '../model';
import { cn } from '@/shared/lib/utils';
import { useBlockDrag } from './useBlockDrag';

type StyleVars = React.CSSProperties & Record<string, string | number>;

interface ActivityBlockProps {
  part: BoardPart;
  laneIndex: number;
  laneCount: number;
  dayStart: number;
  readOnly: boolean;
  selected: boolean;
  onMove: (uid: string, dayShift: number, hourShift: number) => void;
  onResize: (uid: string, hourDelta: number) => void;
  onOpen: (uid: string) => void;
  onDragStateChange: (dragging: boolean) => void;
}

export function ActivityBlock({
  part,
  laneIndex,
  laneCount,
  dayStart,
  readOnly,
  selected,
  onMove,
  onResize,
  onOpen,
  onDragStateChange,
}: ActivityBlockProps) {
  const ref = useRef<HTMLDivElement>(null);
  const startHour = part.startHour ?? dayStart;
  const endHour = startHour + part.hours;
  const clipped = dayStart > NIGHT_START && startHour < dayStart && endHour > dayStart;
  const top = clipped ? HEAD_PAD : HEAD_PAD + hoursToPx(startHour - dayStart);
  const height = clipped
    ? Math.max(16, hoursToPx(endHour - dayStart))
    : Math.max(34, hoursToPx(part.hours));

  const { onBodyMouseDown, onResizeMouseDown } = useBlockDrag({
    ref,
    uid: part.uid,
    height,
    readOnly,
    onMove,
    onResize,
    onOpen,
    onDragStateChange,
  });

  const style: StyleVars = {
    top,
    height,
    '--act-color': TYPE_COLOR[part.type],
    '--lane-index': laneIndex,
    '--lane-count': laneCount,
  };

  return (
    <div
      ref={ref}
      className={cn('tp-act', clipped && 'tp-act--clipped', selected && 'tp-act--selected')}
      style={style}
      onMouseDown={onBodyMouseDown}
      title={`${part.name} · ${fmtH(startHour)}–${fmtH(endHour)} · ${part.hours}h`}
    >
      {clipped ? <div className="tp-act-clipcut" aria-hidden="true" /> : null}
      <div className="tp-act-accent" aria-hidden="true" />
      <div className="tp-act-body">
        <div className="tp-act-icon">
          <TypeIcon type={part.type} transportKind={part.transportKind} size={12} />
        </div>
        <div className="tp-act-text">
          <div className="tp-act-label">{part.name}</div>
          <div className="tp-act-time">
            {fmtH(startHour)} – {fmtH(endHour)}
            <span className="tp-act-dur"> · {part.hours}h</span>
          </div>
        </div>
      </div>
      {readOnly ? null : (
        <div className="tp-act-resize" onMouseDown={onResizeMouseDown} aria-label="Resize" />
      )}
    </div>
  );
}
